import React, { useContext } from 'react';
import {
  Drawer,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Avatar,
  Tooltip,
  MenuItem,
  Box,
  Button,
  Container,
  Popover,
  MenuList,
  Link as MuiLink,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import logo from '../../assets/img/ae.webp';
import { Link, useNavigate } from 'react-router-dom';
import { Appcontext, AppDpx } from '../../context/AppContext';
import { useTranslation } from 'react-i18next';
import { tUser } from '../../store/types';
import { PROFILE_SET } from '../../context/Action';
import { headerStyles } from '../../assets/styles/home';
import { aefinance } from '../../store/aefinance';
import { LinkedIn } from '@mui/icons-material';

const pages = [
  'Home',
  'About',
  'Services',
  'Contact',
  'Appointment',
  'Blogs',
  'Payment',
  'Quote',
];
const settings = ['Admin', 'Logout'];

const styles = {
  button: {
    padding: '.5em 1.875em',
    borderRadius: '50px',
    backgroundColor: '#77A34F',
    color: 'white',
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: 'secondary.dark',
    },
  },
  topBar: {
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontSize: '0.9rem',
    py: 0.5,
    borderBottom: '1px solid #e0e0e0',
  },
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  socialIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '& a': {
      color: '#333',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: '#77A34F',
      },
    },
  },
};

interface Props {
  user?: tUser;
  headerType?: string;
}

function Header(props: Props) {
  const you = useContext(Appcontext).user;
  const dispatch = useContext(AppDpx);
  const langRef = React.useRef(null);
  const user = props?.user || you;

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [openLangPopover, setOpenLangPopover] = React.useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const changeLanguage = (lng: string) => i18n.changeLanguage(lng);
  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const _doSetting = (s: string) => {
    setAnchorElUser(null);
    if (s.toLowerCase() === 'logout') {
      localStorage.removeItem('user');
      dispatch({ type: PROFILE_SET, data: null });
      navigate('/admin/login');
      return;
    }
    navigate(`/${s}`);
  };

  const goToPage = (page: string) => {
    navigate(
      `/${
        page.toLowerCase() === 'home'
          ? ''
          : page.toLowerCase().replace(/\s/g, '')
      }`
    );
  };

  return (
    <>
      {/* 🌿 Top Contact Bar */}
      <Box sx={styles.topBar}>
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={styles.contactInfo}>
            <PhoneIcon fontSize="small" />
            <MuiLink
              href={`tel:${aefinance.contact.phone}`}
              color="inherit"
              underline="none"
              sx={{ ml: 0.5 }}
            >
              {aefinance.contact.phone}
            </MuiLink>
            <EmailIcon fontSize="small" sx={{ ml: 2 }} />
            <MuiLink
              href={`mailto:${aefinance.contact.email}`}
              color="inherit"
              underline="none"
              sx={{ ml: 0.5 }}
            >
              {aefinance.contact.email}
            </MuiLink>
          </Box>
          <Box sx={styles.socialIcons}>
            <MuiLink
              href="https://www.facebook.com/aefinancialservicesllc"
              target="_blank"
            >
              <FacebookIcon fontSize="small" />
            </MuiLink>
            <MuiLink
              href="https://www.linkedin.com/company/13993115/"
              target="_blank"
            >
              <LinkedIn fontSize="small" />
            </MuiLink>
            <MuiLink
              href="https://www.instagram.com/aefinancialservices"
              target="_blank"
            >
              <InstagramIcon fontSize="small" />
            </MuiLink>
          </Box>
        </Container>
      </Box>

      {/* 🌿 Main Navbar */}
      <Box
        sx={[
          (props.headerType === 'fixed' || !props.headerType) &&
            headerStyles.fixed,
          {
            position: props.headerType
              ? props?.headerType === 'absolute'
                ? 'absolute'
                : props?.headerType === 'relative'
                  ? 'relative'
                  : 'fixed'
              : 'fixed',
            backgroundColor: 'white',
            zIndex: 999,
            width: '100%',
          },
        ]}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo */}
            <Box component="a" href="/" sx={headerStyles.imgContainer}>
              <img
                src={logo}
                alt="logo"
                style={headerStyles.img as React.CSSProperties | undefined}
              />
            </Box>

            {/* Mobile Menu Icon */}
            <Box sx={headerStyles.iconButton}>
              <IconButton size="large" onClick={handleOpenNavMenu}>
                <MenuIcon fontSize="large" />
              </IconButton>
              <Drawer
                anchor="left"
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  ...headerStyles.drawer,
                  zIndex: (theme) => theme.zIndex.appBar + 100,
                }}
                PaperProps={{
                  sx: headerStyles.paper,
                }}
              >
                <Box>
                  <Box sx={headerStyles.imgContainerMobile}>
                    <img src={logo} alt="logo" style={headerStyles.imgMobile} />
                  </Box>
                  {pages.map((page) => (
                    <MenuItem
                      key={page}
                      onClick={() => {
                        goToPage(page);
                        handleCloseNavMenu();
                      }}
                      sx={headerStyles.menuItem}
                    >
                      <Typography textAlign="center">
                        {t(`header.pages.${page.toLowerCase()}`)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Box>
              </Drawer>
            </Box>

            {/* Desktop Menu */}
            <Box sx={headerStyles.menuList}>
              {pages.map((page) => (
                <Button
                  sx={headerStyles.mobileBtn}
                  key={page}
                  onClick={() => goToPage(page)}
                >
                  {t(`header.pages.${page.toLowerCase()}`)}
                </Button>
              ))}
            </Box>

            {/* Language + User */}
            <Box sx={headerStyles.flex}>
              <Button
                ref={langRef}
                onClick={() => setOpenLangPopover(true)}
                sx={headerStyles.mr_1}
              >
                {i18n.language === 'es' ? 'Español(es)' : 'English(en)'}
              </Button>
              <Popover
                anchorEl={langRef.current}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                onClose={() => setOpenLangPopover(false)}
                open={openLangPopover}
                PaperProps={{ sx: { minWidth: '150px' } }}
              >
                <MenuList>
                  <MenuItem onClick={() => changeLanguage('en')}>
                    English (en)
                  </MenuItem>
                  <MenuItem onClick={() => changeLanguage('es')}>
                    Spanish (es)
                  </MenuItem>
                </MenuList>
              </Popover>

              {user?.auth ? (
                <>
                  <Tooltip title={t(`header.settings.tooltip`)}>
                    <IconButton onClick={handleOpenUserMenu}>
                      <Avatar alt="User" src="/static/images/avatar/2.webp" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={headerStyles.mt_45}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {settings.map((setting) => (
                      <MenuItem
                        key={setting}
                        onClick={() => _doSetting(setting)}
                      >
                        <Typography textAlign="center">
                          {t(`header.settings.${setting.toLowerCase()}`)}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                <Link to={'/admin/login'}>
                  <Typography textAlign="center" sx={styles.button}>
                    Login
                  </Typography>
                </Link>
              )}
            </Box>
          </Toolbar>
        </Container>
      </Box>
    </>
  );
}

export default Header;
